import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import { studentApi } from '@/api';
import { resolveFileUrl } from '@/api/client';
import { Button, Card, CardHeader, EmptyState, Icon, useToast } from '@/components/ui';
import { errorMessage } from '@/hooks/useApiError';

import { videoFileError } from './videoFile';

/**
 * A short video of the student presenting themselves: shown to companies on
 * their profile. Upload, replace or remove it; the profile's read view and
 * shareable page show it once it is there.
 */
export function ProfileVideoField({ videoUrl }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const input = useRef(null);
  const [problem, setProblem] = useState(null);

  function refresh(updated) {
    queryClient.setQueryData(['student-profile'], updated);
    void queryClient.invalidateQueries({ queryKey: ['student-portfolio-view'] });
  }

  const upload = useMutation({
    mutationFn: (file) => studentApi.uploadVideo(file),
    onSuccess: (updated) => {
      toast.success('Video uploaded');
      refresh(updated);
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not upload the video')),
  });

  const remove = useMutation({
    mutationFn: () => studentApi.removeVideo(),
    onSuccess: (updated) => {
      toast.success('Video removed');
      refresh(updated);
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not remove the video')),
  });

  function pick(event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const found = videoFileError(file);
    setProblem(found);
    if (!found) upload.mutate(file);
  }

  return (
    <Card>
      <CardHeader
        title="Video presentation"
        description="Introduce yourself in a minute or two: who you are and what you want to do next. Companies see it on your profile."
      />
      <input
        ref={input}
        type="file"
        accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov"
        className="hidden"
        aria-label="Video presentation file"
        onChange={pick}
      />

      {videoUrl ? (
        <video
          key={videoUrl}
          src={resolveFileUrl(videoUrl)}
          controls
          preload="metadata"
          className="bg-night aspect-video w-full rounded-lg"
        >
          Your browser cannot play this video.
        </video>
      ) : (
        <EmptyState
          compact
          icon={<Icon name="video" size={18} />}
          title="No video yet"
          description="MP4, WebM or MOV, up to 50 MB."
        />
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button isLoading={upload.isPending} onClick={() => input.current?.click()}>
          <Icon name="upload" size={15} />
          {videoUrl ? 'Replace video' : 'Upload video'}
        </Button>
        {videoUrl && (
          <Button
            variant="ghostDanger"
            isLoading={remove.isPending}
            onClick={() => remove.mutate()}
          >
            Remove
          </Button>
        )}
        {upload.isPending && (
          <span className="text-ink-muted text-sm">Uploading — this can take a minute.</span>
        )}
      </div>
      {problem && (
        <p className="text-caption text-danger-600 mt-2" role="alert">
          {problem}
        </p>
      )}
    </Card>
  );
}
